/**
 * Pre-baked pattern reference index for the predrift PreToolUse hook.
 *
 * At build/setup time: scans .cai/context/*.md and .cai/patterns/*.md,
 * extracts file path mentions (same KNOWN_EXTENSIONS regex as claims.ts),
 * and writes .cai/.cache/pattern-refs.json.
 *
 * At hook time: the index is read synchronously by `cai hook predrift`,
 * which does a fast lookup for the edited file path and emits the matching
 * scaffold content as additionalContext. No SQLite, no Node boot penalty beyond
 * the cai process itself.
 *
 * Index shape:
 *   { "src/auth/middleware.ts": ["context/architecture.md", "patterns/auth-flow.md"] }
 *
 * Keys are relative paths (no leading ./), values are scaffold-relative paths.
 */
import { existsSync, readdirSync, readFileSync, writeFileSync, mkdirSync } from "node:fs";
import { createHash } from "node:crypto";
import { join, relative, extname } from "node:path";
import { KNOWN_EXTENSIONS } from "../utils/extensions.js";

export interface PredriftIndex {
  [filePath: string]: string[]; // filePath → scaffold files that mention it
}

// Match inline code and code block file paths — reuse same heuristic as claims extractor
const PATH_PATTERN = /`([^`]{3,80})`/g;

/**
 * Build the predrift index from the scaffold's context and pattern files.
 * Writes to .cai/.cache/pattern-refs.json.
 * Returns the number of unique file paths indexed.
 */
export function buildPredriftIndex(scaffoldRoot: string): number {
  const cacheDir = join(scaffoldRoot, ".cache");
  mkdirSync(cacheDir, { recursive: true });

  const index: PredriftIndex = {};

  const dirs = ["context", "patterns"];
  for (const dir of dirs) {
    const dirPath = join(scaffoldRoot, dir);
    if (!existsSync(dirPath)) continue;

    const files = readdirSync(dirPath).filter((f) => f.endsWith(".md"));
    for (const file of files) {
      const scaffoldRelPath = `${dir}/${file}`;
      const content = readFileSync(join(dirPath, file), "utf8");

      const matches = content.matchAll(PATH_PATTERN);
      for (const [, captured] of matches) {
        if (!KNOWN_EXTENSIONS.test(captured)) continue;
        if (captured.includes("<") || captured.includes("[")) continue; // template placeholder

        // Normalize: strip leading ./ or /
        const normalized = captured.replace(/^\.?\//, "");
        if (!normalized || normalized.includes("..")) continue;

        if (!index[normalized]) index[normalized] = [];
        if (!index[normalized].includes(scaffoldRelPath)) {
          index[normalized].push(scaffoldRelPath);
        }
      }
    }
  }

  const indexPath = join(cacheDir, "pattern-refs.json");
  writeFileSync(indexPath, JSON.stringify(index, null, 2) + "\n", "utf8");
  return Object.keys(index).length;
}

/**
 * Look up which scaffold files mention a given source file path.
 * Tries exact match, then basename match for files in any subdirectory.
 * Returns scaffold-relative paths.
 */
export function lookupPredrift(indexPath: string, editedPath: string): string[] {
  if (!existsSync(indexPath)) return [];

  let index: PredriftIndex;
  try {
    index = JSON.parse(readFileSync(indexPath, "utf8")) as PredriftIndex;
  } catch {
    return [];
  }

  // Normalize the edited path to relative
  const normalized = editedPath.replace(/^\.?\//, "");
  const basename = normalized.split("/").pop() ?? "";

  const direct = index[normalized] ?? [];

  // Also check basename-only matches (e.g. "middleware.ts" found in index as "src/auth/middleware.ts")
  const byBasename: string[] = [];
  for (const [key, val] of Object.entries(index)) {
    const keyBase = key.split("/").pop() ?? "";
    if (keyBase === basename && key !== normalized) {
      for (const v of val) {
        if (!direct.includes(v) && !byBasename.includes(v)) byBasename.push(v);
      }
    }
  }

  return [...direct, ...byBasename];
}

/**
 * Per-path session marker key for the predrift guidanceOnce throttle.
 *
 * Previously the throttle was per-session (`/tmp/cai-predrift-${sessionId}`), so only
 * the FIRST edited file in a session got scaffold guidance — every later file in the
 * same run (the "80% problem") got nothing. Keying on a short hash of the normalized
 * source path means each distinct file gets its referencing scaffold content exactly
 * once, while still throttling repeated edits to the SAME file.
 */
export function predriftMarkerKey(sessionId: string, filePath: string): string {
  const normalized = filePath.replace(/^\.?\//, "");
  const hash = createHash("md5").update(normalized).digest("hex").slice(0, 10);
  return `/tmp/cai-predrift-${sessionId}-${hash}`;
}

/**
 * Per-session injection counter path. /tmp markers are never cleaned up, so per-path
 * keying multiplies the leak. This counter lets the hook cap total injections per
 * session and stop creating new markers past the cap.
 */
export function predriftCountKey(sessionId: string): string {
  return `/tmp/cai-predrift-count-${sessionId}`;
}

/** Max distinct files that get an injection per session. Bounds the /tmp marker leak. */
export const PREDRIFT_SESSION_CAP = 25;

/** Read the current per-session injection count (0 when absent/unreadable). */
function readInjectionCount(sessionId: string): number {
  try {
    const n = parseInt(readFileSync(predriftCountKey(sessionId), "utf8").trim(), 10);
    return isNaN(n) ? 0 : n;
  } catch {
    return 0;
  }
}

/** Bump the per-session injection count. Never throws. */
function bumpInjectionCount(sessionId: string, current: number): void {
  try {
    writeFileSync(predriftCountKey(sessionId), String(current + 1), "utf8");
  } catch {
    // best-effort — the per-path marker already prevents re-injection for this file
  }
}

/**
 * Run the predrift hook logic: read tool_input from stdin (JSON),
 * extract the file being edited, look up scaffold references,
 * and emit additionalContext JSON if relevant context found.
 *
 * guidanceOnce: skip if /tmp/cai-predrift-$SESSION already exists.
 * Uses CLAUDE_SESSION_ID env var (Claude Code sets this for every hook invocation).
 *
 * Exit codes:
 *   0 — allow (with or without additionalContext)
 *   All errors are swallowed to avoid blocking the tool call.
 */
export async function runPredriftHook(scaffoldRoot: string): Promise<void> {
  const sessionId = process.env.CLAUDE_SESSION_ID ?? "nosession";

  let toolInput: Record<string, unknown>;
  try {
    const raw = readFileSync("/dev/stdin", "utf8");
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    toolInput = (parsed.tool_input ?? parsed) as Record<string, unknown>;
  } catch {
    process.exit(0);
  }

  // Extract file path from tool_input
  const filePath = String(toolInput.path ?? toolInput.file_path ?? "");
  if (!filePath) process.exit(0);

  // Only trigger for source files, not scaffold files
  const ext = extname(filePath);
  if (!KNOWN_EXTENSIONS.test(ext) || filePath.includes(".cai/")) process.exit(0);

  // guidanceOnce throttling — once PER FILE per session, capped per session.
  const markerPath = predriftMarkerKey(sessionId, filePath);
  if (existsSync(markerPath)) {
    // Already injected for this file this session — passthrough silently
    process.exit(0);
  }
  const injectionCount = readInjectionCount(sessionId);
  if (injectionCount >= PREDRIFT_SESSION_CAP) {
    // Session cap reached — stop creating new markers to bound the /tmp leak
    process.exit(0);
  }

  const indexPath = join(scaffoldRoot, ".cache", "pattern-refs.json");
  const refs = lookupPredrift(indexPath, filePath);
  if (refs.length === 0) process.exit(0);

  // Load content from all matching scaffold files, patterns first, cap total at 3000 chars
  const sections: string[] = [];
  let totalChars = 0;
  const MAX_TOTAL = 3000;
  const MAX_PER_FILE = 1500;

  // Prioritize pattern files over context files
  const sorted = [...refs].sort((a, b) => {
    const aIsPattern = a.startsWith("patterns/") ? 0 : 1;
    const bIsPattern = b.startsWith("patterns/") ? 0 : 1;
    return aIsPattern - bIsPattern;
  });

  for (const scaffoldFile of sorted) {
    if (totalChars >= MAX_TOTAL) break;
    const scaffoldFilePath = join(scaffoldRoot, scaffoldFile);
    if (!existsSync(scaffoldFilePath)) continue;
    try {
      const raw = readFileSync(scaffoldFilePath, "utf8")
        .replace(/^---[\s\S]*?---\n/, "")
        .trim()
        .slice(0, MAX_PER_FILE);
      sections.push(`### ${scaffoldFile}\n${raw}`);
      totalChars += raw.length;
    } catch {
      // skip unreadable files
    }
  }

  if (sections.length === 0) process.exit(0);

  const content = sections.join("\n\n");

  // Mark this file as injected (O_EXCL semantics via writeFileSync + exclusive flag)
  try {
    writeFileSync(markerPath, sessionId, { flag: "wx" });
  } catch {
    // Race: another hook beat us — skip
    process.exit(0);
  }
  bumpInjectionCount(sessionId, injectionCount);

  // Emit additionalContext to stdout
  const additionalContext = {
    type: "context",
    content: `[CAI context for ${relative(process.cwd(), filePath) || filePath}]\n\n${content}`,
  };
  process.stdout.write(JSON.stringify(additionalContext) + "\n");
  process.exit(0);
}
